/**
 * Rupee formatting for display.
 *
 * NO FLOATS, ANYWHERE. Amounts arrive as decimal STRINGS from the contract precisely so they never
 * pass through a JS number (CLAUDE.md invariant 2), and formatting is the easiest place to throw
 * that away — `Number('4200.00').toLocaleString()` is a rounding bug waiting for a large enough
 * payroll figure. So this works on the string: split on the decimal point, group the integer half
 * by character position, reassemble. Nothing is parsed into a number at any point.
 *
 * INDIAN DIGIT GROUPING, NOT THOUSANDS: ₹1,42,850 — the last three digits, then pairs (lakh,
 * crore). `Intl.NumberFormat('en-IN')` does this correctly in a browser, but the mobile app's copy
 * of this cannot rely on Intl (Hermes ships reduced locale data), and a figure that groups one way
 * on web and another on a phone is worse than either. Same algorithm both sides, by hand.
 *
 * Deliberately duplicated in apps/mobile/src/lib/money.ts rather than shared: @hrms/ui is web-only
 * per the architecture map, and @hrms/shared is the CONTRACT, not a utility bag. Two small copies
 * of eleven lines beats either violation.
 */

/** Groups an integer-digit string the Indian way: 1234567 -> 12,34,567. */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const lastThree = digits.slice(-3);
  const rest = digits.slice(0, -3);
  // Pairs from the right, which is what makes it lakh/crore rather than thousands.
  const pairs = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${pairs},${lastThree}`;
}

export interface FormatMoneyOptions {
  /** Show the paise. Off by default: a table of ₹4,200.00 is mostly trailing zeros. */
  paise?: boolean;
  symbol?: boolean;
}

const SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  GBP: '£',
  EUR: '€',
  AED: 'AED ',
  SGD: 'S$',
  CAD: 'C$',
  AUD: 'A$',
};

/** `formatMoney('4200.00', 'INR')` -> `₹4,200`. */
export function formatMoney(
  amount: string,
  currency: string,
  { paise = false, symbol = true }: FormatMoneyOptions = {},
): string {
  const negative = amount.startsWith('-');
  const unsigned = negative ? amount.slice(1) : amount;
  const [whole = '0', fraction = ''] = unsigned.split('.');

  const grouped = groupIndian(whole);
  // Two places is the display convention; the wire carries four, and truncating for display is
  // fine where rounding the stored value never is.
  const tail = paise ? `.${(fraction + '00').slice(0, 2)}` : '';
  const prefix = symbol ? (SYMBOLS[currency] ?? `${currency} `) : '';

  return `${negative ? '-' : ''}${prefix}${grouped}${tail}`;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * `formatDateOnly('2026-10-22')` -> `22 Oct 2026`.
 *
 * Formatted from the STRING, never `new Date()`: '2026-10-22' through the Date constructor is UTC
 * midnight rendered in the viewer's zone, which shows 21 Oct to anyone west of Greenwich. The date
 * of an expense is the same date everywhere (CLAUDE.md invariant 3).
 */
export function formatDateOnly(date: string): string {
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) return date;
  const name = MONTHS[Number(month) - 1];
  return name ? `${day} ${name} ${year}` : date;
}

/**
 * `formatInstant('2026-09-12T17:27:02.335Z')` -> `12 Sep 2026, 5:27 pm`.
 *
 * UNLIKE `formatDateOnly`, rendering an INSTANT in the viewer's own local time zone is exactly
 * correct here, not a bug — this is "when did this actually happen" (a workflow decision, a
 * settlement), not "what calendar day is it for this employee" (invariant 3's own distinction). Two
 * viewers in different timezones seeing a decided-at moment rendered in their own local time is the
 * expected reading of a timestamp, the same as any "2 hours ago"-style UI.
 */
export function formatInstant(instant: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  const timePart = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  return `${formatInstantDate(instant)}, ${timePart.toLowerCase()}`;
}

/**
 * `formatInstantDate('2026-09-12T17:27:02.335Z')` -> `12 Sep 2026`: `formatInstant` without the
 * time, for a period label ("28 Aug 2026 – 27 Sep 2026") where the minute is noise.
 */
export function formatInstantDate(instant: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  // Month NAME from our own table, not Intl's `month: 'short'`: newer ICU data renders en-GB
  // September as "Sept", so the same screen said "27 Sept 2026, 11:39 pm" next to
  // `formatDateOnly`'s "27 Sep 2026". The day/month/year NUMBERS still come from Intl, in the
  // viewer's own zone, exactly as before.
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('day')} ${MONTHS[Number(part('month')) - 1] ?? ''} ${part('year')}`;
}
